import React from 'react';
import * as Icons from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';

const Section = ({ id, tag, title, children, className = '' }) => (
  <section id={id} className={`relative py-24 lg:py-32 ${className}`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px w-12 bg-[#004aad]" />
        <span className="text-[#5b8def] text-xs tracking-[0.35em] font-semibold">
          {tag}
        </span>
      </div>
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-12 max-w-3xl">
        {title}
      </h2>
      {children}
    </div>
  </section>
);

export const WhatSection = ({ t }) => (
  <Section id="what" tag={t.what.tag} title={t.what.title}>
    <p className="text-lg text-zinc-400 max-w-3xl mb-14 leading-relaxed">
      {t.what.description}
    </p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {t.what.services.map((s) => {
        const Icon = Icons[s.icon] || Icons.Box;
        return (
          <div
            key={s.title}
            className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#004aad]/50"
          >
            <div className="h-14 w-14 rounded-xl bg-[#004aad]/10 group-hover:bg-[#004aad] flex items-center justify-center mb-6 transition-colors duration-300 border border-[#004aad]/30">
              <Icon
                className="h-7 w-7 text-[#5b8def] group-hover:text-white transition-colors"
                strokeWidth={1.5}
              />
            </div>
            <h3 className="text-white font-semibold text-lg leading-snug mb-2">
              {s.title}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
            <div className="absolute bottom-0 left-7 right-7 h-px bg-gradient-to-r from-transparent via-[#004aad] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        );
      })}
    </div>
  </Section>
);

export const WhySection = ({ t }) => (
  <Section id="why" tag={t.why.tag} title={t.why.title} className="bg-zinc-950">
    <div className="grid lg:grid-cols-2 gap-16 items-start">
      <div>
        <p className="text-2xl md:text-3xl font-light text-white leading-snug mb-6">
          {t.why.lead}
        </p>
        <p className="text-zinc-400 leading-relaxed text-lg">{t.why.paragraph}</p>
      </div>
      <div className="space-y-4">
        {t.why.bullets.map((b) => (
          <div
            key={b}
            className="flex gap-4 p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent hover:border-[#004aad]/40 transition-colors"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-[#004aad] flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
            <p className="text-white pt-1.5 leading-relaxed">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

export const HowSection = ({ t }) => (
  <Section id="how" tag={t.how.tag} title={t.how.title}>
    <p className="text-lg text-zinc-400 max-w-3xl mb-14 leading-relaxed">{t.how.lead}</p>
    <div className="relative">
      <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#004aad]/50 to-transparent" />
      <div className="grid md:grid-cols-3 gap-8">
        {t.how.steps.map((step) => (
          <div key={step.step} className="relative">
            <div className="relative z-10 h-24 w-24 rounded-full bg-black border border-[#004aad] flex items-center justify-center mb-6 mx-auto md:mx-0">
              <span className="text-[#5b8def] font-bold text-2xl">{step.step}</span>
              <div className="absolute inset-0 rounded-full bg-[#004aad]/20 blur-xl -z-10" />
            </div>
            <h3 className="text-white text-2xl font-bold mb-3">{step.title}</h3>
            <p className="text-zinc-400 leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

export const WhoSection = ({ t }) => (
  <Section id="who" tag={t.who.tag} title={t.who.title} className="bg-zinc-950">
    <div className="grid lg:grid-cols-5 gap-12">
      <div className="lg:col-span-3 space-y-6">
        {t.who.paragraphs.map((p) => (
          <p key={p.slice(0, 24)} className="text-zinc-300 text-lg leading-relaxed">
            {p}
          </p>
        ))}
        <div className="mt-8 border-l-4 border-[#004aad] pl-6">
          <p className="text-xl text-white font-medium leading-relaxed">
            {t.who.callout}
          </p>
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#004aad]/15 to-transparent p-8 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#004aad]/30 blur-3xl rounded-full" />
          <Icons.Users className="h-10 w-10 text-[#5b8def] mb-6" strokeWidth={1.5} />
          <div className="text-5xl font-extrabold text-white mb-2">2023</div>
          <div className="text-zinc-400">Year founded</div>
          <div className="mt-8 grid grid-cols-2 gap-6">
            <div>
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-xs text-zinc-500 mt-1">Client focus</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">CA • US</div>
              <div className="text-xs text-zinc-500 mt-1">Coverage</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

export default Section;
